import { mirrorX } from "apparatus/draw";
import { INFINITE_BOUNDS } from "apparatus/driver";
import { createApparatus } from "apparatus/library";
import { P, Segments } from "apparatus/library/common";
import { BasicShapes } from "apparatus/library/shapes/common";
import Flags from "flags";
import { CompoundPath, Group, Path } from "paper";
export class Curve {
    constructor() {
        this.shouldUseBoundsCenter = true;
        this.drivers = Curve.drivers;
    }
    render(a) {
        const { path, xray } = drawCurve(a);
        BasicShapes.StrokeOnly.apply(path, a);
        return {
            graphic: path,
            hitShape: { type: "stroke", path: path.clone() },
            xray,
            pivot: P(0, 0),
            snapping: !Flags.isShapeSnappingEnabled ? undefined : [
                { type: "shape_end", at: P(a.end1x, a.end1y) },
                { type: "shape_end", at: P(a.end2x, a.end2y) },
            ]
        };
    }
}
Curve.properties = {
    label: "Curve",
    defaultAppearance: Object.assign({ end1x: -150, end1y: 0, pivot1x: -40, pivot1y: -80, end2x: 200, end2y: 0, pivot2x: 140, pivot2y: 40 }, BasicShapes.StrokeOnly.defaultAppearance),
    appearanceSpecs: [
        ...BasicShapes.StrokeOnly.specs
    ],
    actions: [
        {
            type: "appearance", label: "Straighten", icon: "pipe",
            action: (a) => {
                // Determine the direction from end1 to end2
                let p1 = P(a.end1x, a.end1y);
                let p2 = P(a.end2x, a.end2y);
                let delta = p2.subtract(p1);
                delta.length = 50; // Set distance of the handles from their parent node.
                let pivot1 = p1.add(delta);
                let pivot2 = p2.subtract(delta);
                return {
                    pivot1x: pivot1.x,
                    pivot1y: pivot1.y,
                    pivot2x: pivot2.x,
                    pivot2y: pivot2.y,
                };
            }
        },
        {
            type: "replace", label: "Convert to line", icon: "horizontal-rule",
            action: (a, p) => {
                let apparatus = createApparatus("line", p);
                let line = {
                    end1x: a.end1x,
                    end1y: a.end1y,
                    end2x: a.end2x,
                    end2y: a.end2y,
                    dashed: apparatus.appearance["dashed"],
                    stroke: a.stroke,
                    strokeThickness: a.strokeThickness,
                };
                apparatus.appearance = line;
                return apparatus;
            }
        }
    ]
};
Curve.drivers = [
    Object.assign(Object.assign({ type: "rectangle" }, INFINITE_BOUNDS), { toAppearance: (f, p) => f({ end1x: p.x, end1y: p.y }), fromAppearance: a => P(a.end1x, a.end1y), scale: 1.0, snapping: !Flags.isShapeSnappingEnabled ? undefined
            : (_, point) => ({ type: "shape_end", at: point }) }),
    Object.assign(Object.assign({ type: "rectangle" }, INFINITE_BOUNDS), { toAppearance: (f, p) => f({ pivot1x: p.x, pivot1y: p.y }), fromAppearance: a => P(a.pivot1x, a.pivot1y), scale: 1.0 }),
    Object.assign(Object.assign({ type: "rectangle" }, INFINITE_BOUNDS), { toAppearance: (f, p) => f({ end2x: p.x, end2y: p.y }), fromAppearance: a => P(a.end2x, a.end2y), scale: 1.0, snapping: !Flags.isShapeSnappingEnabled ? undefined
            : (_, point) => ({ type: "shape_end", at: point }) }),
    Object.assign(Object.assign({ type: "rectangle" }, INFINITE_BOUNDS), { toAppearance: (f, p) => f({ pivot2x: p.x, pivot2y: p.y }), fromAppearance: a => P(a.pivot2x, a.pivot2y), scale: 1.0 }),
];
export class CurvedArrow {
    constructor() {
        this.shouldUseBoundsCenter = true;
        this.drivers = Curve.drivers;
    }
    render(a) {
        const { path, xray } = drawCurve(a);
        const graphic = new Group([path]);
        const drawEndArrow = a.arrows === "both" || a.arrows === "end";
        const drawStartArrow = a.arrows === "both" || a.arrows === "start";
        if (drawEndArrow) {
            graphic.addChild(drawArrowHead(path.segments[1], "handleIn", a.size));
        }
        if (drawStartArrow) {
            graphic.addChild(drawArrowHead(path.segments[0], "handleOut", a.size));
        }
        BasicShapes.StrokeOnly.apply(graphic, a);
        return {
            graphic,
            hitShape: { type: "stroke", path: path.clone() },
            xray,
            pivot: P(0, 0),
            snapping: !Flags.isShapeSnappingEnabled ? undefined : [
                { type: "shape_end", at: P(a.end1x, a.end1y) },
                { type: "shape_end", at: P(a.end2x, a.end2y) },
            ]
        };
    }
}
CurvedArrow.properties = {
    label: "Curved Arrow",
    defaultAppearance: Object.assign(Object.assign({}, Curve.properties.defaultAppearance), { arrows: "end", size: 15 }),
    appearanceSpecs: [
        ...Curve.properties.appearanceSpecs,
        BasicShapes.header,
        {
            spec: "toggle-buttons",
            size: "icon",
            key: "arrows",
            label: "Arrows",
            options: [
                { icon: "long-arrow-left", label: "Start", value: "start" },
                { icon: "long-arrow-right", label: "End", value: "end" },
                { icon: "arrows-h", label: "Both", value: "both" },
            ]
        },
        {
            spec: "slider", key: "size", label: "Arrow size",
            min: 10, max: 50
        }
    ],
};
export class StirArrow {
    render(a) {
        const path = new Path(Segments([[10, 0], , [19, 2]], [[16, 20], [18, -5], [-8, 2]]));
        mirrorX(path);
        const drawEndArrow = a.arrows === "both" || a.arrows === "end";
        const drawStartArrow = a.arrows === "both" || a.arrows === "start";
        const graphic = new Group([path]);
        graphic.scale(a.size / 50);
        if (drawEndArrow) {
            graphic.addChild(drawArrowHead(path.segments[path.segments.length - 1], "handleIn", 10));
        }
        if (drawStartArrow) {
            graphic.addChild(drawArrowHead(path.segments[0], "handleOut", 10));
        }
        BasicShapes.StrokeOnly.apply(graphic, a);
        return {
            graphic,
            hitShape: graphic.rectHitShape(),
            pivot: path.bounds.center,
        };
    }
}
StirArrow.properties = {
    label: "Stirring motion arrow",
    defaultAppearance: {
        arrows: "both",
        size: 50,
        stroke: "#777777",
        strokeThickness: 3.0,
    },
    appearanceSpecs: [
        ...BasicShapes.StrokeOnly.specs,
        BasicShapes.header,
        {
            spec: "toggle-buttons",
            size: "icon",
            key: "arrows",
            label: "Arrows",
            options: [
                { icon: "long-arrow-right", label: "Clockwise", value: "end" },
                { icon: "long-arrow-left", label: "Anti-clockwise", value: "start" },
                { icon: "arrows-h", label: "Both", value: "both" },
            ]
        },
        {
            spec: "slider", key: "size", label: "Size",
            min: 30, max: 120
        }
    ],
};
export function drawCurve(a, opts) {
    var _a;
    const joinPivots = (_a = opts === null || opts === void 0 ? void 0 : opts.joinPivots) !== null && _a !== void 0 ? _a : true;
    const path = new Path(Segments([[a.end1x, a.end1y], , [a.pivot1x - a.end1x, a.pivot1y - a.end1y]], [[a.end2x, a.end2y], [a.pivot2x - a.end2x, a.pivot2y - a.end2y]]));
    const xray = new CompoundPath({});
    xray.moveTo(P(a.end1x, a.end1y));
    xray.lineTo(P(a.pivot1x, a.pivot1y));
    if (joinPivots) {
        xray.lineTo(P(a.pivot2x, a.pivot2y));
    }
    else {
        xray.moveTo(P(a.pivot2x, a.pivot2y));
    }
    xray.lineTo(P(a.end2x, a.end2y));
    return { path, xray };
}
export function drawArrowHead(segment, normalKey, size, opts) {
    var _a;
    const normal = segment[normalKey].normalize();
    const adjustAngle = (_a = opts === null || opts === void 0 ? void 0 : opts.adjustAngle) !== null && _a !== void 0 ? _a : 0;
    const p1 = normal.rotate(45 + adjustAngle).multiply(size).add(segment.point);
    const p2 = normal.rotate(-45 + adjustAngle).multiply(size).add(segment.point);
    return new Path([p1, segment.point, p2]);
}
